import { useState, useMemo } from 'react';
import { Wallet, LogOut, ChevronDown, ExternalLink } from 'lucide-react';
import { useWallet } from '@/hooks/useWallet';
import { cn, truncateAddress } from '@/lib/utils';
import { Button } from './Button';

export function WalletButton() {
  const {
    address,
    isConnected,
    isConnecting,
    connect,
    disconnect,
    connectors,
    connectorName,
  } = useWallet();

  const [showConnectors, setShowConnectors] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  // Sort connectors: detected (installed) first, then not detected
  const sortedConnectors = useMemo(() => {
    const detected: typeof connectors = [];
    const notDetected: typeof connectors = [];

    connectors.forEach(connector => {
      if (connector.available()) {
        detected.push(connector);
      } else {
        notDetected.push(connector);
      }
    });

    return [...detected, ...notDetected];
  }, [connectors]);

  if (isConnected && address) {
    return (
      <div className="relative">
        <button
          onClick={() => setShowMenu(!showMenu)}
          className={cn(
            'flex items-center gap-2 px-4 py-2 rounded-lg',
            'bg-secondary border border-border',
            'hover:border-primary/30 transition-all'
          )}
        >
          <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
          <span className="font-mono text-sm">{truncateAddress(address)}</span>
          <ChevronDown className={cn(
            'w-4 h-4 transition-transform',
            showMenu && 'rotate-180'
          )} />
        </button>

        {showMenu && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setShowMenu(false)}
            />
            <div className="absolute right-0 mt-2 w-56 z-50 bg-card border border-border rounded-lg shadow-xl overflow-hidden animate-fade-in">
              <div className="p-3 border-b border-border">
                <p className="text-xs text-muted-foreground">Connected with {connectorName}</p>
                <p className="font-mono text-sm mt-1">{truncateAddress(address, 10, 8)}</p>
              </div>

              <div className="p-2">
                <a
                  href={`https://sepolia.voyager.online/contract/${address}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-secondary transition-colors text-sm"
                >
                  <ExternalLink className="w-4 h-4" />
                  View on Explorer
                </a>

                <button
                  onClick={() => {
                    disconnect();
                    setShowMenu(false);
                  }}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-secondary transition-colors text-sm text-destructive w-full"
                >
                  <LogOut className="w-4 h-4" />
                  Disconnect
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <Button
        onClick={() => setShowConnectors(!showConnectors)}
        loading={isConnecting}
        className="gap-2"
      >
        <Wallet className="w-4 h-4" />
        Connect Wallet
      </Button>

      {showConnectors && !isConnecting && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowConnectors(false)}
          />
          <div className="absolute right-0 mt-2 w-72 z-50 bg-card border border-border rounded-xl shadow-xl overflow-hidden animate-fade-in">
            <div className="p-4 border-b border-border">
              <p className="font-semibold text-lg">Connect a wallet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Choose your preferred wallet
              </p>
            </div>

            <div className="p-2 space-y-1 max-h-80 overflow-y-auto">
              {sortedConnectors.map((connector) => {
                const isDetected = connector.available();

                return (
                  <button
                    key={connector.id}
                    onClick={() => {
                      connect({ connector });
                      setShowConnectors(false);
                    }}
                    className={cn(
                      "flex items-center gap-3 w-full px-3 py-3 rounded-lg transition-colors",
                      "hover:bg-secondary"
                    )}
                  >
                    {connector.icon ? (
                      <img
                        src={typeof connector.icon === 'string' ? connector.icon : connector.icon.dark}
                        alt=""
                        className="w-10 h-10 rounded-xl"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center">
                        <Wallet className="w-5 h-5 text-muted-foreground" />
                      </div>
                    )}
                    <span className="font-medium flex-1 text-left">{connector.name}</span>
                    {isDetected && (
                      <span className="text-xs text-success font-medium">
                        Detected
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
